import { useCallback, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { QueryResult } from 'react-apollo';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { fg } from '@confluence/feature-gating';

import { PageTreeQuery } from './queries/PageTreeQuery.graphql';
import type { PageTreeQuery as PageTreeQueryResponse } from './queries/__types__/PageTreeQuery';
import type { PageTreeRootLevelPagesQuery as PageTreeRootLevelPagesResponse } from './queries/__types__/PageTreeRootLevelPagesQuery';
import { PageTreeRootLevelPagesQuery as PageTreeRootLevelQuery } from './queries/PageTreeRootLevelQuery.graphql';
import { INITIAL_PAGE_SIZE } from './paginationLimits';
import type { PageTreeState } from './usePageTreeState';
import { usePageTreeState } from './usePageTreeState';
import { flattenTreeData, deriveStateFromData } from './data-transformers';
import { isFollowingNodeHidden } from './data-extractors';
import { useLoadMorePages } from './useLoadMorePages';
import { usePageTreeChildren } from './usePageTreeChildren';
import { PAGE_TREE_STATUSES } from './pageTreeStatuses';
import type { GraphQLPageStatus } from './pageTreeStatuses';

export const generateQueryArgs = (
	targetPage,
	paginated,
	forceFetch,
	detached,
	spaceKey,
	isSuperAdmin,
) => {
	const query = targetPage ? PageTreeQuery : PageTreeRootLevelQuery;

	const defaultVariables = targetPage
		? {
				spaceKey,
				isSuperAdmin,
				statuses: PAGE_TREE_STATUSES,
				ptStatuses: PAGE_TREE_STATUSES,
				contentId: targetPage,
				isReplacePagesWithPTPageEnabled: true,
			}
		: {
				spaceKey,
				isSuperAdmin,
				statuses: PAGE_TREE_STATUSES,
				emojiTitleEnabled: true,
				isReplacePagesWithPTPageEnabled: true,
			};

	const variablesForPagination = {
		paginationLimit: INITIAL_PAGE_SIZE,
		...defaultVariables,
	};

	return {
		query,
		options: {
			fetchPolicy: detached ? 'no-cache' : forceFetch ? 'network-only' : 'cache-first',
			variables: paginated === true || !targetPage ? variablesForPagination : defaultVariables,
		},
	};
};

export const usePageTreeData = (
	spaceKey?: string,
	isSuperAdmin?: boolean,
	statuses?: GraphQLPageStatus[],
	onLoadMore?: (length, direction?: 'following' | 'previous') => void,
): {
	loadPreviousPages: () => void;
	loadFollowingPages: () => void;
	reloadChildren: () => void;
	queryChildren: any;
} => {
	const [state, actions] = usePageTreeState();
	const { focusedPageId } = state as PageTreeState;
	const { loadMorePages } = useLoadMorePages({ statuses });
	const { reloadChildren, queryChildren } = usePageTreeChildren({
		statuses,
		isSuperAdmin,
	});

	const loadMoreDirectionalPages = useCallback(
		async (direction: 'previous' | 'following') => {
			actions.setIsPageTreeLoading(true);

			// Edge Case: if there are hidden following ancestors, we need to load previous as well as following siblings if applicable
			const followingNodeHidden = isFollowingNodeHidden(state.initialPages);
			let pages = state.initialPages;
			if (direction === 'following' && followingNodeHidden) {
				const { mergedPages: prevPages, error: prevError } = await loadMorePages(
					focusedPageId,
					spaceKey,
					isSuperAdmin,
					state.cursors,
					pages,
					'previous',
				);

				if (prevError) {
					//toDo: handle loading previous / next errors here, then reset state so they can attempt to try again
					return;
				}

				pages = prevPages;
			}

			const { mergedPages, rootLevelPages, error } = await loadMorePages(
				focusedPageId,
				spaceKey,
				isSuperAdmin,
				state.cursors,
				pages,
				direction,
			);

			if (error) {
				//toDo: handle loading previous / next errors here, then reset state so they can attempt to try again
				return;
			}

			const newState = deriveStateFromData(
				{ currentPage: mergedPages, rootLevelPages, space: state.space },
				spaceKey,
				state,
				false,
				undefined,
				false,
			);

			actions.setStateWithUIAttributes(newState);
			const flattenedPages = flattenTreeData(mergedPages, rootLevelPages);
			actions.setIsPageTreeLoading(false);
			if (onLoadMore) {
				onLoadMore(Object.keys(flattenedPages).length, direction);
			}
		},
		[actions, loadMorePages, focusedPageId, isSuperAdmin, onLoadMore, spaceKey, state],
	);

	const loadPreviousPages = useCallback(
		() => loadMoreDirectionalPages('previous'),
		[loadMoreDirectionalPages],
	);
	const loadFollowingPages = useCallback(
		() => loadMoreDirectionalPages('following'),
		[loadMoreDirectionalPages],
	);

	return {
		loadPreviousPages,
		loadFollowingPages,
		reloadChildren,
		queryChildren,
	};
};

export const useFetchPages = (
	currentPageId: string | undefined,
	paginated: boolean,
	forceFetch: boolean,
	detached: boolean,
	spaceKey?: string,
	isSuperAdmin?: boolean,
): QueryResult<PageTreeQueryResponse | PageTreeRootLevelPagesResponse> => {
	const { query, options } = useMemo(
		() => generateQueryArgs(currentPageId, paginated, forceFetch, detached, spaceKey, isSuperAdmin),
		[currentPageId, paginated, forceFetch, detached, spaceKey, isSuperAdmin],
	);

	return useQuery<PageTreeQueryResponse | PageTreeRootLevelPagesResponse>(query, {
		skip: true,
		fetchPolicy: options.fetchPolicy as WatchQueryFetchPolicy,
	});
};

export const useLoadPagesCallback = (
	spaceKey,
	expandSpaceHomepage,
	syntheticItem,
	isPeekingFromBlogs = false,
	isSuperAdmin = false,
	statuses,
	syntheticItemIsDraft,
	pageTreeStateUpdatesContainer,
) => {
	const [state, actions] = usePageTreeState();
	const { queryChildren } = usePageTreeChildren({
		statuses,
		isSuperAdmin,
	});
	return useCallback(
		(data: any) => {
			if (data && Object.keys(data).length > 0) {
				const spaceHomePageId = data.space?.homepage?.id;
				const newState = deriveStateFromData(
					data,
					spaceKey,
					state,
					isPeekingFromBlogs,
					syntheticItem,
					syntheticItemIsDraft,
					pageTreeStateUpdatesContainer,
					fg('confluence_frontend_whiteboard_emoji_titles'),
				);

				actions.setStateWithUIAttributes(newState);
				const { rootId, pages: allPages } = newState;

				if (
					expandSpaceHomepage &&
					spaceHomePageId &&
					rootId !== spaceHomePageId &&
					allPages[spaceHomePageId]
				) {
					return actions.expandPage(allPages[spaceHomePageId], queryChildren);
				}
				return allPages;
			}
			return data;
		},
		[
			spaceKey,
			state,
			isPeekingFromBlogs,
			syntheticItem,
			syntheticItemIsDraft,
			actions,
			expandSpaceHomepage,
			queryChildren,
			pageTreeStateUpdatesContainer,
		],
	);
};
